import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { BulkActionsBarButton, BulkActionsBarEvents } from '../types';
import { ButtonColor, ButtonColors, ButtonTypes } from '../../gzButton/types';
import { SelectOption } from '../../gzSelect/types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { debounce } from '../../../utils/event.utils';
import { isElementOverflowed } from '../../../utils/htmlElement.utils';
import { translateFn } from '../../../utils/translator.helper';

@customElement('gz-bulk-actions-bar')
export class GzBulkActionsBarWebComponent extends LitElement {
  @property({ type: Array, attribute: true, reflect: true })
  buttons: BulkActionsBarButton[] = [];

  @property({ type: String, attribute: true, reflect: true })
  dropdownText = translateFn('More actions');

  @property({ type: Number, attribute: true, reflect: true })
  trailingButtonsCount = 0;

  @state()
  private displayedButtonsCount = 0;

  @state()
  private loading = true;

  @query('#buttonsContainer')
  private buttonsContainer: HTMLElement;

  private resizeObserver: ResizeObserver;

  private mutationObserver: MutationObserver;

  private debouncedStopLoading = debounce(this.stopLoading.bind(this), 300);

  static get styles() {
    return styles;
  }

  firstUpdated(): void {
    this.resizeObserver = new ResizeObserver(debounce(this.handleResize.bind(this), 300));
    this.resizeObserver.observe(this.buttonsContainer);

    this.mutationObserver = new MutationObserver(this.handleMutation.bind(this));
    this.mutationObserver.observe(document.documentElement, { attributes: true, childList: true, subtree: true });
  }

  updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('trailingButtonsCount') && changedProperties.get('trailingButtonsCount') !== undefined) {
      this.handleResize();
    }
    if (
      changedProperties.has('displayedButtonsCount') &&
      changedProperties.get('displayedButtonsCount') !== undefined
    ) {
      const canIncrease = changedProperties.get('displayedButtonsCount') < this.displayedButtonsCount;
      this.updateDisplayedButtons(canIncrease);
    }
  }

  disconnectedCallback(): void {
    this.resizeObserver.unobserve(this.buttonsContainer);
    this.mutationObserver.disconnect();
  }

  render(): TemplateResult {
    const [firstButtons, dropDownButtons, lastButtons] = this.groupButtons();
    return html`
      <div class="wrapper">
        ${this.renderLoader()}
        <div id="buttonsContainer" class="buttons-container" style="opacity: ${this.loading ? 0 : 1};">
          ${firstButtons.map(button => this.renderButton(button))} ${this.renderDropdown(dropDownButtons)}
          ${lastButtons.map(button => this.renderButton(button))}
        </div>
      </div>
    `;
  }

  private renderLoader(): TemplateResult | undefined {
    if (!this.loading) {
      return;
    }
    return html`<gz-loader class="loader"></gz-loader>`;
  }

  private renderButton(button: BulkActionsBarButton): TemplateResult {
    const { text, value, color, type, disabled, action } = { ...button };
    return html`
      <gz-button
        .color="${color ?? ButtonColors.SECONDARY}"
        .type="${type ?? ButtonTypes.QUIET}"
        ?disabled="${disabled}"
        @click="${() => this.onButtonClick(value, action)}"
      >
        ${text}
      </gz-button>
    `;
  }

  private renderDropdown(dropdownButtons: BulkActionsBarButton[]): TemplateResult | undefined {
    if (dropdownButtons.length === 0) {
      return;
    }
    return html`
      <gz-select
        overlayPositionX="left"
        .options="${this.computeDropdownOptions(dropdownButtons)}"
        @selectionChanged="${this.onDropdownOptionClick}"
      >
        <gz-button color="${ButtonColors.SECONDARY}" type="${ButtonTypes.QUIET}">
          <div class="dropdown-button-content">
            ${this.dropdownText}
            <gz-icon
              class="dropdown-icon"
              name="ds-icon-textbox_decrease"
              size="24"
              color="${ButtonColors.SECONDARY}"
            ></gz-icon>
          </div>
        </gz-button>
      </gz-select>
    `;
  }

  private getDropdownOptionCustomClass(buttonColor: ButtonColor | undefined): string {
    switch (buttonColor) {
      case ButtonColors.PRIMARY:
        return 'info';
      case ButtonColors.NEGATIVE:
        return 'danger';
      default:
        return '';
    }
  }

  private groupButtons(): BulkActionsBarButton[][] {
    const trailingButtons = Math.max(0, Math.min(4, this.trailingButtonsCount));
    const firstButtons = this.buttons.slice(0, Math.max(this.displayedButtonsCount - trailingButtons, 0));
    const dropDownButtons = this.buttons.slice(
      Math.max(this.displayedButtonsCount - trailingButtons, 0),
      this.buttons.length - Math.min(trailingButtons, this.displayedButtonsCount)
    );
    const lastButtons = this.buttons.slice(
      this.buttons.length - Math.min(trailingButtons, this.displayedButtonsCount),
      this.buttons.length
    );
    return [firstButtons, dropDownButtons, lastButtons];
  }

  private computeDropdownOptions(dropdownButtons: BulkActionsBarButton[]): SelectOption[] {
    return dropdownButtons.map((button, index) => ({
      title: button.text.toUpperCase(),
      value: `${index + this.displayedButtonsCount}`,
      customClass: this.getDropdownOptionCustomClass(button.color),
      disabled: button.disabled,
    }));
  }

  private onDropdownOptionClick(event: WebComponentEvent): void {
    const index = parseInt(event.detail.selected, 10);
    this.onButtonClick(this.buttons[index].value, this.buttons[index].action);
  }

  private onButtonClick(value: string | undefined, action: Function | undefined): void {
    this.dispatchEvent(
      new CustomEvent(BulkActionsBarEvents.CLICK, {
        detail: { value },
      })
    );
    action?.();
  }

  private handleResize(): void {
    this.loading = true;
    this.updateDisplayedButtons();
  }

  private handleMutation(mutationList: MutationRecord[]): void {
    for (const mutation of mutationList) {
      if (this.isFontSizeMutation(mutation)) {
        this.handleResize();
      }
    }
  }

  private isFontSizeMutation(mutation: MutationRecord): boolean {
    const styleAttributeValue = (mutation.target as HTMLElement).style?.getPropertyValue('--dynamic-font-size-changer');
    return mutation.type === 'attributes' && mutation.attributeName === 'style' && !!styleAttributeValue;
  }

  private stopLoading(): void {
    if (isElementOverflowed(this.buttonsContainer)) {
      this.updateDisplayedButtons();
      return;
    }
    setTimeout(() => {
      this.loading = false;
    }, 500); // timeout present s.t. we have a minimum amount in which the loader is shown
  }

  private updateDisplayedButtons(canIncrease = true): void {
    this.debouncedStopLoading();
    if (isElementOverflowed(this.buttonsContainer)) {
      this.displayedButtonsCount = Math.max(0, this.displayedButtonsCount - 1);
    } else if (canIncrease) {
      this.displayedButtonsCount = Math.min(this.buttons.length > 5 ? 4 : 5, this.displayedButtonsCount + 1);
    }
  }
}
